import React from 'react';
import { pricingPlans } from '../data/mock';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Check, Star } from 'lucide-react';

export const Pricing = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1B4F72] to-[#138808] text-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold mb-6">Simple, Transparent Pricing</h1>
          <p className="text-xl text-gray-100 max-w-3xl mx-auto leading-relaxed">
            Choose the plan that best fits your travel needs. No hidden fees, cancel anytime.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {pricingPlans.map((plan) => (
              <Card
                key={plan.id}
                className={`border-0 shadow-xl hover:shadow-2xl transition-all duration-300 ${
                  plan.recommended
                    ? 'ring-4 ring-[#FF9933] transform scale-105'
                    : 'hover:-translate-y-2'
                }`}
              >
                <CardContent className="p-8">
                  {plan.recommended && (
                    <div className="flex items-center justify-center gap-2 mb-4">
                      <Star className="text-[#FF9933] fill-current" size={20} />
                      <span className="text-[#FF9933] font-semibold text-sm uppercase tracking-wide">
                        Most Popular
                      </span>
                      <Star className="text-[#FF9933] fill-current" size={20} />
                    </div>
                  )}
                  
                  <h3 className="text-2xl font-bold text-[#1B4F72] mb-2 text-center">{plan.name}</h3>
                  
                  <div className="text-center mb-6">
                    {typeof plan.price === 'number' ? (
                      <>
                        <span className="text-5xl font-bold text-[#1B4F72]">₹{plan.price}</span>
                        <span className="text-gray-500">/{plan.period}</span>
                      </>
                    ) : (
                      <span className="text-4xl font-bold text-[#1B4F72]">{plan.price}</span>
                    )}
                  </div>

                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <Check className="text-[#138808] mr-3 flex-shrink-0 mt-1" size={20} />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    className={`w-full py-6 text-lg font-semibold transition-all duration-300 ${
                      plan.recommended
                        ? 'bg-[#FF9933] hover:bg-[#E68A2E] text-white shadow-lg hover:shadow-xl hover:scale-105'
                        : 'bg-gray-100 hover:bg-gray-200 text-[#1B4F72]'
                    }`}
                  >
                    {plan.price === 0 ? 'Get Started Free' : plan.price === 'Custom' ? 'Contact Sales' : 'Subscribe Now'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-[#1B4F72] mb-12 text-center">Pricing FAQs</h2>
          <div className="space-y-6">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-[#1B4F72] mb-3">Can I switch plans later?</h3>
                <p className="text-gray-600 leading-relaxed">
                  Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and we'll prorate any charges.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-[#1B4F72] mb-3">What payment methods do you accept?</h3>
                <p className="text-gray-600 leading-relaxed">
                  We accept all major credit/debit cards, UPI, net banking, and popular digital wallets like Paytm, PhonePe, and Google Pay.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-[#1B4F72] mb-3">Is there a free trial for Pro plan?</h3>
                <p className="text-gray-600 leading-relaxed">
                  Yes! New users get a 14-day free trial of the Pro plan. No credit card required to start.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-[#1B4F72] mb-3">What's included in Enterprise plan?</h3>
                <p className="text-gray-600 leading-relaxed">
                  Enterprise plans are customized based on your organization's needs. Contact our sales team for a detailed quote and demo.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};
